import React, { useEffect, useMemo, useState } from "react";
import CustomSelect from "react-select";
import {
  components,
  type MenuListProps,
  type GroupBase,
  type OptionProps,
} from "react-select";
import styled, { useTheme } from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import {
  Box,
  Flex,
  Icon,
  Modal,
  Small,
  TextInput,
  Button,
} from "orcs-design-system";

import icons from "src/config/icons";
import customStyles from "src/components/Nav/WorkspaceSwitcher/customStyles";
import { getDesignOptionLabel } from "src/pages/Designs/designOptionLabels";
import ConfirmOptionActionModal from "src/pages/Designs/ConfirmOptionActionModal";

type DesignOption = {
  id: string;
  name: string;
  isTrunk: boolean;
};

type PendingAction =
  | { kind: "create" }
  | { kind: "rename"; option: DesignOption }
  | { kind: "delete"; option: DesignOption };

type SelectOption = {
  value: string;
  label: string;
  option: DesignOption;
};

type DesignOptionSelectProps = {
  frozen: boolean;
  creating: boolean;
  renaming: boolean;
  onOpenCreate: () => void;
  onOpenRename: (option: DesignOption) => void;
  onOpenDelete: (option: DesignOption) => void;
};

const stopMenuClose = (event: React.MouseEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

const OptionActionButton = styled.button<{ $danger?: boolean }>`
  appearance: none;
  border: none;
  background: transparent;
  padding: 0;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  border-radius: ${themeGet("radii.1")};
  color: ${(props) =>
    props.$danger
      ? themeGet("colors.danger")(props)
      : themeGet("colors.subText")(props)};

  &:hover {
    background: ${themeGet("colors.hoverSurface")};
    color: ${(props) =>
      props.$danger
        ? themeGet("colors.dangerDark")(props)
        : themeGet("colors.primary")(props)};
  }

  &:focus-visible {
    outline: ${themeGet("borderWidths.1")} solid
      ${themeGet("colors.focusOutline")};
    outline-offset: 1px;
  }

  svg {
    width: 11px;
    height: 11px;
  }
`;

/** Matches the two 18px action buttons + 2px gap on overlay rows. */
const OPTION_ACTIONS_WIDTH = "38px";
const OPTION_ROW_MIN_HEIGHT = "18px";
const optionRowPadding = (props: { theme: object }) =>
  `${themeGet("space.xxs")(props)} ${themeGet("space.s")(props)}`;

const OptionActionsSpacer = styled(Box).attrs({
  "aria-hidden": true,
})`
  width: ${OPTION_ACTIONS_WIDTH};
  flex-shrink: 0;
`;

const MenuFooter = styled(Box)`
  margin: ${themeGet("space.xs")} -${themeGet("space.xs")} -${themeGet("space.xs")};
  border-top: 1px solid ${themeGet("colors.surfaceRaisedBorder")};
`;

const AddOptionButton = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${themeGet("space.xxs")};
  padding: ${optionRowPadding};
  min-height: calc(${OPTION_ROW_MIN_HEIGHT} + ${themeGet("space.xxs")} * 2);
  cursor: pointer;
  border-radius: ${themeGet("radii.1")};
  color: ${themeGet("colors.subText")};
  font-size: ${themeGet("fontSizes.0")};
  line-height: 1.2;
  box-sizing: border-box;

  &:hover {
    background: ${themeGet("colors.hoverSurface")};
    color: ${themeGet("colors.primary")};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    width: 10px;
    height: 10px;
  }
`;

const SwitcherRoot = styled(Flex)`
  align-items: center;
  line-height: 1;
`;

/** AppNav AppName styles size all descendant svgs to navBarLogoSize (42px). */
const SwitcherIcon = styled.span`
  display: inline-flex;
  align-items: center;
  line-height: 0;

  && svg {
    width: 10px;
    height: 10px;
  }
`;

const SwitcherLabel = styled(Small)`
  color: ${themeGet("colors.subText")};
  font-size: ${themeGet("fontSizes.0")};
  line-height: 1;
  white-space: nowrap;
`;

const SelectWrap = styled(Box)`
  display: flex;
  align-items: center;
  min-height: 20px;
`;

type DesignOptionRowProps = OptionProps<
  SelectOption,
  false,
  GroupBase<SelectOption>
> & {
  selectProps: OptionProps<
    SelectOption,
    false,
    GroupBase<SelectOption>
  >["selectProps"] &
    DesignOptionSelectProps;
};

const DesignOptionRow = (props: DesignOptionRowProps) => {
  const { frozen, onOpenRename, onOpenDelete } = props.selectProps;
  const { option: designOption } = props.data;

  return (
    <components.Option {...props}>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        gap="xs"
        minHeight={OPTION_ROW_MIN_HEIGHT}
        width="100%"
      >
        <Small flex="1" minWidth="0" lineHeight="1.2">
          {props.label}
        </Small>
        {!frozen && !designOption.isTrunk ? (
          <Flex
            alignItems="center"
            gap="2px"
            flexShrink={0}
            onMouseDown={stopMenuClose}
          >
            <OptionActionButton
              type="button"
              aria-label={`Rename ${designOption.name}`}
              onClick={() => onOpenRename(designOption)}
            >
              <Icon icon={icons.pen} />
            </OptionActionButton>
            <OptionActionButton
              type="button"
              $danger
              aria-label={`Delete ${designOption.name}`}
              onClick={() => onOpenDelete(designOption)}
            >
              <Icon icon={icons.trash} />
            </OptionActionButton>
          </Flex>
        ) : !frozen ? (
          <OptionActionsSpacer />
        ) : null}
      </Flex>
    </components.Option>
  );
};

type DesignOptionMenuListProps = MenuListProps<
  SelectOption,
  false,
  GroupBase<SelectOption>
> & {
  selectProps: MenuListProps<
    SelectOption,
    false,
    GroupBase<SelectOption>
  >["selectProps"] &
    DesignOptionSelectProps;
};

const DesignOptionMenuList = (props: DesignOptionMenuListProps) => {
  const { frozen, creating, onOpenCreate } = props.selectProps;

  return (
    <components.MenuList {...props}>
      {props.children}
      {!frozen ? (
        <MenuFooter>
          <AddOptionButton
            type="button"
            disabled={creating}
            onMouseDown={stopMenuClose}
            onClick={onOpenCreate}
            data-testid="design-option-add"
          >
            <Icon icon={icons.plus} />
            New option…
          </AddOptionButton>
        </MenuFooter>
      ) : null}
    </components.MenuList>
  );
};

type DesignOptionControlsProps = {
  optionId?: string | undefined;
  setOptionId: (nextOptionId?: string) => void;
  options: DesignOption[];
  optionsLoading?: boolean;
  baselineDataDate?: string | null;
  frozen: boolean;
  creating: boolean;
  renaming: boolean;
  createOption: (name: string) => Promise<void>;
  renameOption: (option: DesignOption, name: string) => Promise<void>;
  deleteOption: (option: DesignOption) => Promise<void>;
};

const DesignOptionControls = ({
  optionId,
  setOptionId,
  options,
  optionsLoading = false,
  baselineDataDate = null,
  frozen,
  creating,
  renaming,
  createOption,
  renameOption,
  deleteOption,
}: DesignOptionControlsProps) => {
  const theme = useTheme();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [name, setName] = useState("");

  const selectOptions = useMemo(
    () =>
      options.map((option) => ({
        value: option.isTrunk ? "" : option.id,
        label: getDesignOptionLabel(option, baselineDataDate),
        option,
      })),
    [baselineDataDate, options]
  );

  useEffect(() => {
    if (optionsLoading || !options.length) return;
    const storedIsOverlay = options.some(
      (option) => !option.isTrunk && option.id === optionId
    );
    if (optionId && !storedIsOverlay) {
      setOptionId(undefined);
    }
  }, [options, optionsLoading, optionId, setOptionId]);

  const selected =
    selectOptions.find((option) => option.value === (optionId || "")) || null;

  const selectStyles = useMemo(() => {
    const base = customStyles({ theme });
    return {
      ...base,
      menu: (provided: Record<string, unknown>) => ({
        ...base.menu(provided),
        left: "auto",
        width: "auto",
        minWidth: "280px",
      }),
      menuPortal: (provided: Record<string, unknown>) => ({
        ...provided,
        zIndex: 9999,
      }),
      option: (
        provided: Record<string, unknown>,
        state: Parameters<NonNullable<(typeof base)["option"]>>[1]
      ) => ({
        ...base.option(provided, state),
        display: "flex",
        alignItems: "center",
        padding: optionRowPadding({ theme }),
      }),
    };
  }, [theme]);

  const closePending = () => {
    setPending(null);
    setName("");
  };

  const openCreate = () => {
    setMenuIsOpen(false);
    setName("");
    setPending({ kind: "create" });
  };
  const openRename = (option: DesignOption) => {
    setMenuIsOpen(false);
    setName(option.name);
    setPending({ kind: "rename", option });
  };
  const openDelete = (option: DesignOption) => {
    setMenuIsOpen(false);
    setPending({ kind: "delete", option });
  };

  return (
    <>
      <SwitcherRoot
        gap="xs"
        data-testid="design-option-switcher"
        flexShrink={0}
      >
        <SwitcherIcon aria-hidden>
          <Icon icon={["fas", "code-branch"]} color="subText" size="xs" />
        </SwitcherIcon>
        <SwitcherLabel>Design options:</SwitcherLabel>
        <SelectWrap>
          <CustomSelect
            aria-label="Design option"
            id="design-option-switcher"
            data-testid="select-design-option-dropdown"
            placeholder="Design option"
            options={selectOptions}
            value={selected}
            menuIsOpen={menuIsOpen}
            onMenuOpen={() => setMenuIsOpen(true)}
            onMenuClose={() => setMenuIsOpen(false)}
            onChange={(option: SelectOption | null) =>
              setOptionId(option?.value || undefined)
            }
            styles={selectStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            isSearchable={false}
            isOptionSelected={(option) => option.value === (optionId || "")}
            components={{
              Option: DesignOptionRow,
              MenuList: DesignOptionMenuList,
            }}
            {...({
              frozen,
              creating,
              renaming,
              onOpenCreate: openCreate,
              onOpenRename: openRename,
              onOpenDelete: openDelete,
            } satisfies DesignOptionSelectProps)}
          />
        </SelectWrap>
      </SwitcherRoot>

      <Modal
        visible={pending?.kind === "create"}
        onClose={closePending}
        headerContent="New option"
        width="360px"
        footerContent={
          <Flex justifyContent="flex-end" gap="s">
            <Button variant="default" onClick={closePending}>
              Cancel
            </Button>
            <Button
              disabled={creating || !name.trim()}
              onClick={async () => {
                await createOption(name.trim());
                closePending();
              }}
            >
              Create option
            </Button>
          </Flex>
        }
      >
        <TextInput
          id="new-design-option-name"
          label="Option name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={frozen}
          focus={pending?.kind === "create"}
        />
      </Modal>

      <Modal
        visible={pending?.kind === "rename"}
        onClose={closePending}
        headerContent="Rename option"
        footerContent={
          <Flex justifyContent="flex-end" gap="s">
            <Button variant="default" onClick={closePending}>
              Cancel
            </Button>
            <Button
              disabled={renaming || !name.trim()}
              onClick={async () => {
                if (pending?.kind !== "rename") return;
                await renameOption(pending.option, name.trim());
                closePending();
              }}
            >
              Save name
            </Button>
          </Flex>
        }
      >
        <TextInput
          id="rename-design-option"
          label="Option name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={frozen}
          focus={pending?.kind === "rename"}
        />
      </Modal>

      <ConfirmOptionActionModal
        visible={pending?.kind === "delete"}
        option={pending?.kind === "delete" ? pending.option : null}
        baselineDataDate={baselineDataDate}
        frozen={frozen}
        onClose={closePending}
        onConfirm={deleteOption}
      />
    </>
  );
};

export default DesignOptionControls;
